import type { TeleportLimits } from './xrTeleportClient'

export type Point = { x: number; y: number; z: number }
export type BlockLookup = (x: number, y: number, z: number) => { name: string; shapes: readonly (readonly number[])[] } | null
const dangerous = /water|lava|fire|cactus|magma|campfire|powder_snow|sweet_berry|wither_rose|portal/
const lerp = (a: Point, b: Point, t: number): Point => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t })

function bodyClear(point: Point, blockAt: BlockLookup) {
  const min = { x: point.x - 0.3, y: point.y + 0.001, z: point.z - 0.3 }
  const max = { x: point.x + 0.3, y: point.y + 1.8, z: point.z + 0.3 }
  for (let x = Math.floor(min.x); x <= Math.floor(max.x); x++) for (let y = Math.floor(min.y); y <= Math.floor(max.y); y++) for (let z = Math.floor(min.z); z <= Math.floor(max.z); z++) {
    const block = blockAt(x, y, z)
    if (!block || dangerous.test(block.name)) return false
    for (const shape of block.shapes) {
      if (x + shape[0] < max.x && x + shape[3] > min.x && y + shape[1] < max.y && y + shape[4] > min.y && z + shape[2] < max.z && z + shape[5] > min.z) return false
    }
  }
  return true
}

function supported(point: Point, blockAt: BlockLookup) {
  for (const dx of [-0.29, 0, 0.29]) for (const dz of [-0.29, 0, 0.29]) {
    const px = point.x + dx; const pz = point.z + dz
    const x = Math.floor(px); const y = Math.floor(point.y - 0.01); const z = Math.floor(pz)
    const block = blockAt(x, y, z)
    if (!block || dangerous.test(block.name) || !block.shapes.some(s => px >= x + s[0] && px <= x + s[3] && pz >= z + s[2] && pz <= z + s[5] && Math.abs(y + s[4] - point.y) < 0.015)) return false
  }
  return true
}

/** Conservative preview only: server repeats geometry, gameplay and region checks. */
export function validTeleportDestination(from: Point, to: Point, blockAt: BlockLookup, limits: TeleportLimits) {
  const distance = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z)
  if (![from.x, from.y, from.z, to.x, to.y, to.z].every(Number.isFinite) || distance > limits.maxDistance || distance < 0.15 || to.y - from.y > limits.maxRise || from.y - to.y > limits.maxDrop || !supported(to, blockAt)) return false
  const steps = Math.max(1, Math.ceil(distance / 0.1))
  for (let i = 0; i <= steps; i++) {
    const t = i / steps; const point = lerp(from, to, t)
    point.y += 4 * t * (1 - t) // Matches the server's one-metre swept body arc.
    if (!bodyClear(point, blockAt)) return false
  }
  return true
}

/** Intersect each short arc segment with block collision boxes, including slabs. */
function intersectSegment(a: Point, b: Point, blockAt: BlockLookup): { point: Point; top: boolean } | null {
  let closest = Infinity
  let hit: { point: Point; top: boolean } | null = null
  for (let x = Math.floor(Math.min(a.x, b.x)); x <= Math.floor(Math.max(a.x, b.x)); x++) for (let y = Math.floor(Math.min(a.y, b.y)); y <= Math.floor(Math.max(a.y, b.y)); y++) for (let z = Math.floor(Math.min(a.z, b.z)); z <= Math.floor(Math.max(a.z, b.z)); z++) {
    const block = blockAt(x, y, z)
    if (!block) return { point: a, top: false }
    const shapes = dangerous.test(block.name) ? [[0, 0, 0, 1, 1, 1]] : block.shapes
    for (const shape of shapes) {
      let near = 0; let far = 1; let top = false
      for (const [axis, offset, index] of [['x', x, 0], ['y', y, 1], ['z', z, 2]] as const) {
        const delta = b[axis] - a[axis]; const min = offset + shape[index]; const max = offset + shape[index + 3]
        if (Math.abs(delta) < 1e-9) { if (a[axis] < min || a[axis] > max) far = -1; continue }
        const low = (min - a[axis]) / delta; const high = (max - a[axis]) / delta
        const entry = Math.min(low, high)
        if (entry > near) { near = entry; top = axis === 'y' && delta < 0 }
        far = Math.min(far, Math.max(low, high))
      }
      if (near <= far && near < closest) { closest = near; hit = { point: lerp(a, b, near), top: top && !dangerous.test(block.name) } }
    }
  }
  return hit
}

export function findTeleportTarget(origin: Point, direction: Point, feet: Point, blockAt: BlockLookup, limits: TeleportLimits) {
  const points: Point[] = [{ ...origin }]
  let target: Point | null = null
  let valid = false
  for (let step = 1; step <= 80; step++) {
    const time = step * 0.025
    const point = { x: origin.x + direction.x * 7 * time, y: origin.y + direction.y * 7 * time - 4.9 * time * time, z: origin.z + direction.z * 7 * time }
    const hit = intersectSegment(points[points.length - 1], point, blockAt)
    if (hit) { points.push(hit.point); target = hit.point; valid = hit.top && validTeleportDestination(feet, target, blockAt, limits); break }
    points.push(point)
    if (Math.hypot(point.x - feet.x, point.z - feet.z) > limits.maxDistance + 1 || point.y < feet.y - limits.maxDrop - 1) break
  }
  return { points, target, valid }
}
